import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Col, Row, notification } from "antd";
import Icon from "@ant-design/icons";
import { LeftIcon, ToteIcon } from "../../assets/icons/icons";
import Header from "../../components/layout/header";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import { useAddTote } from "../common/mutations";
import Styles from "./registerTrolleyReview.module.scss";
import { getTrolleyStatus } from "../../components/reusable/styles";
import { convertUTCToLocal } from "../../utils/timeUtils";

const RegisterToteReview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toteId = searchParams.get("toteId") || "";
  const msn = searchParams.get("msn") || "";
  const usn = searchParams.get("usn") || "";
  // const itemCount = parseInt(searchParams.get("itemCount") || "1");
  // const weight = parseFloat(searchParams.get("weight") || "1");
  const pointOfDelivery = searchParams.get("pointOfDelivery") || "";
  const dateOfDelivery = searchParams.get("dateOfDelivery") || "";
  const pointOfUse = searchParams.get("pointOfUse") || "";
  const dateOfUse = searchParams.get("dateOfUse") || "";
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [registeredToteRecordId, setRegisteredToteRecordId] = useState<number | null>(null);

  const addToteMutation = useAddTote();
  const PENDING_TOTE_LOAD_KEY = "pendingToteLoadContext";
  const PENDING_LOCATION_TOTE_KEY = "pendingLocationToteContext";

  const handleRegister = async () => {
    // Validate required fields
    if (!pointOfDelivery || !dateOfDelivery || !pointOfUse || !dateOfUse) {
      notification.error({
        message: "Missing Required Fields",
        description: "Please go back and fill in all required fields: Point of Delivery, Date of Delivery, Point of Use / Station, and Date of Use",
        duration: 5,
      });
      navigate(-1);
      return;
    }

    try {
      const response = await addToteMutation.mutateAsync({
        tote_id: toteId,
        msn: msn,
        usn: usn,
        item_count: "1", 
        weight: "1",    
        status: 1, // Default status: Available
        point_of_delivery: pointOfDelivery,
        date_of_delivery: dateOfDelivery,
        point_of_use_station: pointOfUse,
        date_of_use: dateOfUse,
      });
      // Store the record ID from the response
      if (response?.id) {
        setRegisteredToteRecordId(response.id);
      }
      
      // Clear sessionStorage after successful registration
      const STORAGE_KEY = `registerToteFormData_${toteId}`;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn("Failed to clear form data from sessionStorage", error);
      }
      
      setIsSuccessOpen(true);
    } catch {
      notification.error({
        message: "Failed to register the Tote. Please try again later.",
        description: "An error occurred while registering the tote",
        duration: 5,
      });
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);

    // Use the record ID from registration response if available, otherwise fall back to tote_id
    const idToNavigate = registeredToteRecordId 
      ? String(registeredToteRecordId) 
      : toteId;

    // Check for pending location tote context first
    try {
      const locationStored = sessionStorage.getItem(PENDING_LOCATION_TOTE_KEY);
      if (locationStored) {
        const locationParsed = JSON.parse(locationStored) as {
          toteId: string;
          locationId?: string;
          locationBarcode?: string;
        } | null;

        if (locationParsed && locationParsed.toteId === toteId) {
          // Update context with registered tote record ID and flag to open modal
          sessionStorage.setItem(
            PENDING_LOCATION_TOTE_KEY,
            JSON.stringify({
              toteId: idToNavigate, // Use registered record ID
              locationId: locationParsed.locationId,
              locationBarcode: locationParsed.locationBarcode,
              openModal: true, // Flag to indicate modal should be opened
              segment: "Tote", // Ensure Tote tab is selected
            })
          );
          const locationToNavigate =
            locationParsed.locationBarcode || locationParsed.locationId || "";
          if (locationToNavigate) {
            navigate(
              `/location-info/${encodeURIComponent(locationToNavigate)}`
            );
            return;
          }
        }
      }
    } catch (error) {
      console.warn("Failed to resume pending location tote context", error);
    }

    // Check for pending trolley load context
    try {
      const stored = sessionStorage.getItem(PENDING_TOTE_LOAD_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as {
          trolleyId: string;
          toteId: string;
        } | null;

        if (parsed && parsed.toteId === toteId) {
          // Update pending context with the new record ID from registration response
          // Use the record ID if available, otherwise fall back to tote_id string
          const updatedToteId = idToNavigate;
          sessionStorage.setItem(
            PENDING_TOTE_LOAD_KEY,
            JSON.stringify({
              trolleyId: parsed.trolleyId,
              toteId: updatedToteId, // Use the new record ID
            })
          );
          navigate(`/trolley-info/${encodeURIComponent(parsed.trolleyId)}`);
          return;
        }
      }
    } catch (error) {
      console.warn("Failed to resume pending tote load context", error);
    }

    // Fallback to tote info when no pending context exists - use record ID
    navigate(`/tote-info/${encodeURIComponent(idToNavigate)}`);
  };

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <div className={Styles.header}>
          <Icon onClick={() => navigate(-1)} component={LeftIcon} />
          <h2 className={Styles.title}>Review Tote Information</h2>
        </div>

        <div className={Styles.infoSection}>
          <Card className={Styles.infoCard}>
            <div className={Styles.trolleyInfoRow}>
              <Icon component={ToteIcon} className={Styles.icon} />
              <div>
                <div className="labels">Tote No</div>
                <div className="values">{toteId}</div>
              </div>
            </div>

            <Row gutter={[16, 16]} className={Styles.detailsGrid}>
              {/* <Col span={12}>
                <div className="labels">Weight</div>
                <div className={Styles.detailValue}>{weight}kg</div>
              </Col>
              <Col span={12}>
                <div className="labels">Item count</div>
                <div className={Styles.detailValue}>
                  {itemCount.toString().padStart(2, "0")}
                </div>
              </Col> */}
              <Col span={12}>
                <div className="labels">MSN</div>
                <div className={Styles.detailValue}>{msn}</div>
              </Col>
              <Col span={12}>
                <div className="labels">USM</div>
                <div className={Styles.detailValue}>{usn}</div>
              </Col>
              <Col span={12}>
                {" "}
                <div className="labels">Activity Status</div>
                <div
                  style={{ color: getTrolleyStatus("Available") }}
                  className="status"
                >
                  Available
                </div>
              </Col>
              <Col span={12}>
                <div className="labels">Registered Date</div>
                <div className={Styles.detailValue}>
                  {convertUTCToLocal(new Date())}
                </div>
              </Col>
              <Col span={12}>
                <div className="labels">Point of Delivery</div>
                <div className={Styles.detailValue}>
                  {pointOfDelivery || "N/A"}
                </div>
              </Col>
              <Col span={12}>
                <div className="labels">Date of Delivery</div>
                <div className={Styles.detailValue}>
                  {dateOfDelivery ? convertUTCToLocal(dateOfDelivery) : "N/A"}
                </div>
              </Col>
              <Col span={12}>
                <div className="labels">Point of Use / Station</div>
                <div className={Styles.detailValue}>
                  {pointOfUse || "N/A"}
                </div>
              </Col>
              <Col span={12}>
                <div className="labels">Date of Use</div>
                <div className={Styles.detailValue}>
                  {dateOfUse ? convertUTCToLocal(dateOfUse) : "N/A"}
                </div>
              </Col>
            </Row>
          </Card>
        </div>

        <div className={Styles.bottomAction}>
          <Button
            className={Styles.registerButton}
            size="large"
            onClick={handleRegister}
            loading={addToteMutation.isPending}
          >
            Register Tote
          </Button>
        </div>

        <SuccessAlert
          open={isSuccessOpen}
          title="Tote registered successfully"
          message={
            <>
              The Tote (<b style={{ color: "#101010" }}>ID: {toteId}</b>) has
              been successfully registered and is available.
            </>
          }
          buttonText="Done"
          onClose={handleSuccessClose}
        />
      </div>
    </div>
  );
};

export default RegisterToteReview;
